"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import api from "../lib/api";

const NotificationContext = createContext(null);
const POLL_MS = 15000;

export function NotificationProvider({ children }) {
  const { status: sessionStatus } = useSession();
  const [items, setItems] = useState([]);
  const [toast, setToast] = useState(null);
  const [popupEnabled, setPopupEnabled] = useState(true);
  const knownIds = useRef(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    setPopupEnabled(window.localStorage.getItem("nordlicht.notifications.popup") !== "false");
  }, []);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/notifications", { params: { unread: true, limit: 20 } });
      const next = Array.isArray(response.data) ? response.data : [];
      const fresh = next.find((item) => !knownIds.current.has(item.id));

      if (initialized.current && fresh && popupEnabled) {
        setToast(fresh);
      }
      next.forEach((item) => knownIds.current.add(item.id));
      initialized.current = true;
      setItems(next);
    } catch (error) {
      // Notifications dürfen den laufenden Shopfloor nicht unterbrechen.
      console.warn("[Notifications] Polling fehlgeschlagen", error?.message);
    }
  }, [popupEnabled]);

  useEffect(() => {
    if (sessionStatus !== "authenticated") return undefined;

    load();
    const interval = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(interval);
  }, [load, sessionStatus]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = window.setTimeout(() => setToast(null), 9000);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const markRead = useCallback(async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.post("/notifications/mark-all-read");
    setItems([]);
  }, []);

  const value = {
    items,
    unreadCount: items.length,
    loading: false,
    reload: load,
    markRead,
    markAllRead,
    popupEnabled,
    setPopupEnabled: (enabled) => {
      setPopupEnabled(enabled);
      window.localStorage.setItem("nordlicht.notifications.popup", String(enabled));
    },
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {toast && (
        <div className="fixed right-4 top-20 z-[60] w-[min(420px,calc(100vw-2rem))]" role="status">
          <div className={`notification-toast notification-${toast.severity || "info"}`}>
            <div className="flex items-start gap-3">
              <span className="text-xl" aria-hidden="true">{toast.severity === "critical" || toast.severity === "error" ? "⚠" : "🔔"}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{toast.title || "Neue Benachrichtigung"}</div>
                <div className="mt-1 text-sm text-slate-300">{toast.message}</div>
              </div>
              <button className="text-slate-400 hover:text-white" onClick={() => setToast(null)} aria-label="Popup schließen">×</button>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error("useNotificationContext muss innerhalb von NotificationProvider verwendet werden");
  return context;
}
